/**
 * Browser Authorization-Code + PKCE client for the separate-origin Customer
 * Impact Portal (CLAUDE.md #6: native/external surfaces auth via OIDC
 * Authorization Code + PKCE — never embed a secret in the binary).
 *
 * This is net-new: the internal AFFiNE frontend is cookie-session based and has
 * no reusable browser PKCE client. The portal backend `/api/portal/*` is
 * Bearer-only, so the portal mints its own access token via public PKCE (no
 * client secret) and sends it as `Authorization: Bearer`.
 */

export interface PortalAuthConfig {
  /** Keycloak realm issuer, e.g. https://auth.pipe.localhost/realms/pipe */
  issuer: string;
  /** public client id (no secret) */
  clientId: string;
  /** window.location.origin + "/callback" */
  redirectUri: string;
  /** requested scopes, e.g. "openid portal" */
  scope: string;
}

const DEFAULT_ISSUER = 'https://auth.pipe.localhost/realms/pipe';

/**
 * Runtime config: an env-injected global wins (so a deployment can repoint the
 * issuer without a rebuild), otherwise the localhost default.
 */
export function portalAuthConfig(): PortalAuthConfig {
  const injected =
    (globalThis as { PORTAL_AUTH_ISSUER?: string }).PORTAL_AUTH_ISSUER ??
    DEFAULT_ISSUER;
  return {
    issuer: injected,
    clientId: 'pipe-portal',
    redirectUri: window.location.origin + '/callback',
    scope: 'openid portal',
  };
}

const VERIFIER_KEY = 'pipe.portal.pkce.verifier';
const STATE_KEY = 'pipe.portal.pkce.state';
const TOKEN_KEY = 'pipe.portal.access_token';
const ID_TOKEN_KEY = 'pipe.portal.id_token';

/** base64url encode raw bytes (no padding, URL-safe alphabet). */
function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) {
    str += String.fromCharCode(b);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a 43-character (256-bit) base64url code verifier per RFC 7636
 * (length 43..128). Uses the CSPRNG `crypto.getRandomValues`.
 */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Random opaque state for CSRF protection of the redirect round-trip. */
export function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** base64url(sha256(verifier)) — the S256 PKCE code challenge. */
export async function codeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Build the Keycloak authorization-endpoint URL for the redirect.
 * response_type=code, S256 challenge, no secret.
 */
export function buildAuthorizeUrl(
  cfg: PortalAuthConfig,
  challenge: string,
  state: string
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scope,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  return `${cfg.issuer}/protocol/openid-connect/auth?${params.toString()}`;
}

/**
 * Exchange the authorization code for an access token at the token endpoint.
 * Public PKCE: grant_type=authorization_code + code_verifier, NO client_secret.
 */
export async function exchangeCode(
  cfg: PortalAuthConfig,
  code: string,
  verifier: string
): Promise<{ access_token: string; id_token?: string; expires_in?: number }> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    client_id: cfg.clientId,
    code_verifier: verifier,
  });
  const res = await fetch(`${cfg.issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`portal token exchange failed: ${res.status}`);
  }
  return (await res.json()) as {
    access_token: string;
    id_token?: string;
    expires_in?: number;
  };
}

// ---------------------------------------------------------------------------
// Token store (in-memory, mirrored to sessionStorage so a redirect round-trip
// survives the page reload but the token never outlives the browser session).
// ---------------------------------------------------------------------------

let inMemoryToken: string | null = null;

export function getAccessToken(): string | null {
  if (inMemoryToken) {
    return inMemoryToken;
  }
  try {
    inMemoryToken = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    inMemoryToken = null;
  }
  return inMemoryToken;
}

function setAccessToken(token: string): void {
  inMemoryToken = token;
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // sessionStorage may be unavailable (private mode); in-memory still works.
  }
}

export function logout(): void {
  inMemoryToken = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ID_TOKEN_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    // ignore
  }
}

/**
 * RP-initiated logout URL (OIDC end_session). With the stored id_token as a
 * hint Keycloak signs the SSO session out without a confirmation page and
 * bounces back to the portal origin (which then restarts a fresh login).
 */
export function buildEndSessionUrl(
  cfg: PortalAuthConfig = portalAuthConfig()
): string {
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    post_logout_redirect_uri: window.location.origin + '/',
  });
  try {
    const idToken = sessionStorage.getItem(ID_TOKEN_KEY);
    if (idToken) {
      params.set('id_token_hint', idToken);
    }
  } catch {
    // fall back to the Keycloak confirm page
  }
  return `${cfg.issuer}/protocol/openid-connect/logout?${params.toString()}`;
}

/**
 * Begin the login redirect: mint a verifier+state, stash them, and send the
 * browser to Keycloak. Does not return (assigns window.location).
 */
export async function login(
  cfg: PortalAuthConfig = portalAuthConfig()
): Promise<void> {
  const verifier = generateCodeVerifier();
  const state = generateState();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  const challenge = await codeChallenge(verifier);
  window.location.assign(buildAuthorizeUrl(cfg, challenge, state));
}

/**
 * Handle the `/callback` redirect: validate state, exchange the code, store the
 * token. Returns true when a token was obtained.
 */
export async function handleCallback(
  cfg: PortalAuthConfig = portalAuthConfig()
): Promise<boolean> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !verifier || returnedState !== expectedState) {
    return false;
  }
  const { access_token, id_token } = await exchangeCode(cfg, code, verifier);
  setAccessToken(access_token);
  if (id_token) {
    try {
      sessionStorage.setItem(ID_TOKEN_KEY, id_token);
    } catch {
      // private mode: RP-initiated logout falls back to the confirm page.
    }
  }
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return true;
}
